import axios from 'axios';
import { AnyAction } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { Organization } from 'src/models/organization';
import { AppState } from 'src/redux/types';
import APIS from 'src/utils/apiUrl';
import { getAuthToken } from 'src/utils/auth/getAuthToken';
import { authActions } from '../actions';
import { AuthUserInfoController } from '../types';
import { Roles } from '@dataquiver/stoneage-roles';
import { SentinelUserInfo } from './sentinelUserInfo';

/** A thunk action which fetches information about the current user from the portal system. */
export const fetchUserInfo = (): ThunkAction<void, AppState, undefined, AnyAction> => async dispatch => {
  try {
    dispatch(authActions.fetchUserStart());
    const token = await getAuthToken();
    if (!token) throw new Error('Tried to fetch user info without an auth token!');
    const accessToken = token.access.token;
    const opts = { headers: { Authorization: `Bearer ${accessToken}` } };
    const { data }: { data: SentinelUserInfo } = await axios.get(`${APIS.PORTAL_API}/api/users/get`, opts);
    if (!data._id || !data.org || typeof data.role !== 'number')
      throw new Error(`Invalid user info object: ${JSON.stringify(data)}`);

    const { data: org }: { data: Organization } = await axios.get(`${APIS.PORTAL_API}/api/organizations/get`, opts);

    const controllerParams = {
      options: { projection: { _id: 0, ssn: 1, physicalSerial: 1, description: 1, org: 1 } },
      query: {
        ssn: { $ne: null },
        physicalSerial: { $ne: null },
      },
    };

    if (data.role < Roles.roles.STONEAGE_ADMIN) {
      (controllerParams.query as any).org = org._id;
    } else {
      (controllerParams.query as any).org = { $ne: null };
    }

    const { data: controllers }: { data: AuthUserInfoController[] } = await axios.get(
      `${APIS.SENTINEL_API}/api/controllers/list`,
      { ...opts, params: controllerParams },
    );

    dispatch(
      authActions.fetchUserSuccess({
        user: {
          id: data._id,
          org: data.org,
          orgDetails: org,
          controllers: controllers.map(i => ({ ...i, description: i.description || undefined })),
          role: data.role,
          status: data.status,
          givenName: data.given_name ?? undefined,
          familyName: data.family_name ?? undefined,
          email: data.email ?? undefined,
          tosAcceptedAt: data.sentinelLinkTOSAcceptedAt ?? undefined,
        },
      }),
    );
  } catch (e) {
    console.error('Failed to fetch user info', e);
    dispatch(authActions.fetchUserFailure());
  }
};
